import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@shared/lib/http/axiosBaseQuery";
import { AccountHebType } from "@models/types";
import { Account, AccountAdd, AccountEdit, AccountSearch, AccountSchema, AccountsListSchema, AccountsPaginatedSchema, AccountsPaginatedWithRelationsSchema, AccountsResponse } from "@models/account";
import { ACCOUNT_BROWSE_PER_PAGE, getAccountSearchBody, SearchParams } from "./accountsBrowse.utils";
import { stableSerialize } from "@shared/lib/http/stableSerialize";
import { seedDetailCacheFromBrowse } from "@features/tickets/shared/utils/seedDetailCacheFromBrowse";

type AccountBrowseType = NonNullable<AccountSearch["account_type"]>;

type AccountsBrowseBaseArgs = Omit<SearchParams, "filter"> & {
  filter?: Partial<AccountSearch>;
};

export type SearchAccountsBrowseArgs = AccountsBrowseBaseArgs & {
  accountType: AccountBrowseType;
  withRelations?: boolean;
};

export type SearchAccountsBrowseResponse = {
  data: Account[];
  links: AccountsResponse["links"];
  meta: AccountsResponse["meta"];
};

export type AccountMutationTagsArg = {
  accountType?: AccountBrowseType;
  accountId?: number;
};


export const accountsApi = createApi({
  reducerPath: "accountsApi",
  baseQuery: axiosBaseQuery({ baseUrl: "/api/" }),
  tagTypes: ["accounts", "accountsBrowse", "account"],
  endpoints: (b) => ({

    getAccountsByType: b.query<Account[], AccountHebType>({
      query: getAccountsByTypeQuery,
      transformResponse: parseAccountsListResponse,
      providesTags: getAccountsByTypeProvidesTags,
      keepUnusedDataFor: Infinity,
    }),

    searchAccountsBrowse: b.query<SearchAccountsBrowseResponse, SearchAccountsBrowseArgs>({
      query: searchAccountsBrowseQuery,
      serializeQueryArgs: searchAccountsBrowseSerializeQueryArgs,
      transformResponse: searchAccountsBrowseTransformResponse,
      providesTags: searchAccountsBrowseProvidesTags,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        seedDetailCacheFromBrowse(accountsApi, "getAccountById", data.data, dispatch);
      },
    }),

    getAccountById: b.query<Account, string | number>({
      query: getAccountByIdQuery,
      transformResponse: parseAccountResponse,
      providesTags: getAccountByIdProvidesTags,
    }),

    addAccount: b.mutation<Account, AccountAdd>({
      query: addAccountQuery,
      invalidatesTags: addAccountInvalidatesTags,
    }),

    saveAccount: b.mutation<Account, { accountId: number; accountToSave: AccountEdit; accountType?: AccountBrowseType}>({
      query: saveAccountQuery,
      invalidatesTags: saveAccountInvalidatesTags,
    }),

    deleteAccount: b.mutation<void, { accountId: number; accountType?: AccountBrowseType }>({
      query: deleteAccountQuery,
      invalidatesTags: deleteAccountInvalidatesTags,
    }),

    getNextAccountNumber: b.query<number, AccountBrowseType>({
      query: getNextAccountNumberQuery,
      transformResponse: getNextAccountNumberTransformResponse,
    }),

    accountRecommendation: b.query<number, number>({
      query: accountRecommendationQuery,
      transformResponse: accountRecommendationTransformResponse,
    }),
  }),
});


export const {
  useGetAccountsByTypeQuery,
  useLazyGetAccountsByTypeQuery,
  useSearchAccountsBrowseQuery,
  useLazySearchAccountsBrowseQuery,
  useGetAccountByIdQuery,
  useLazyGetAccountByIdQuery,
  useAddAccountMutation,
  useSaveAccountMutation,
  useDeleteAccountMutation,
  useLazyGetNextAccountNumberQuery,
  useLazyAccountRecommendationQuery,
} = accountsApi;


/**
 * Loads all income accounts for dropdowns and lookups.
 * @returns ReturnType<typeof accountsApi.useGetAccountsByTypeQuery> - RTK Query result for income accounts.
 */
export function useGetIncomesAccountsQuery() {

  return accountsApi.useGetAccountsByTypeQuery("הכנסה");
}


/**
 * Loads all outcome accounts for dropdowns and lookups.
 * @returns ReturnType<typeof accountsApi.useGetAccountsByTypeQuery> - RTK Query result for outcome accounts.
 */
export function useGetOutcomesAccountsQuery() {

  return accountsApi.useGetAccountsByTypeQuery("הוצאה");
}


/**
 * Loads all bank accounts for dropdowns and lookups.
 * @returns ReturnType<typeof accountsApi.useGetAccountsByTypeQuery> - RTK Query result for bank accounts.
 */
export function useGetBanksAccountsQuery() {

  return accountsApi.useGetAccountsByTypeQuery("בנק");
}


/**
 * Loads all cash box accounts for dropdowns and lookups.
 * @returns ReturnType<typeof accountsApi.useGetAccountsByTypeQuery> - RTK Query result for cash box accounts.
 */
export function useGetCashBoxesAccountsQuery() {

  return accountsApi.useGetAccountsByTypeQuery("קופה");
}


/**
 * Loads all system accounts for dropdowns and lookups.
 * @returns ReturnType<typeof accountsApi.useGetAccountsByTypeQuery> - RTK Query result for system accounts.
 */
export function useGetSystemAccountsQuery() {

  return accountsApi.useGetAccountsByTypeQuery("מערכת");
}





//         *******************************************
//         *             helper functions            *
//         *******************************************



/**
 * RTK Query cache tags to invalidate after account create, update, or delete.
 * @param arg AccountMutationTagsArg - Account type and/or id to scope invalidation.
 * @returns Array - Cache tag descriptors for typed lists, browse lists, and optional detail.
 */
function accountMutationTags({ accountType, accountId }: AccountMutationTagsArg) {

  return [
    ...(accountType ? [
      { type: "accounts" as const, id: accountType },
      { type: "accountsBrowse" as const, id: accountType }
    ] : []),

    ...(accountId ? [
      { type: "account" as const, id: accountId }
    ] : []),
  ];
}


/**
 * Validates a single account API payload with Zod.
 * @param response unknown - Raw response body from axiosBaseQuery.
 * @returns Account - Parsed account entity.
 */
function parseAccountResponse(response: unknown): Account {

  return AccountSchema.parse(response);
}


/**
 * Validates an accounts list payload and returns the data array.
 * @param response unknown - Raw response body from axiosBaseQuery.
 * @returns Account[] - Parsed account rows from the list response.
 */
function parseAccountsListResponse(response: unknown): Account[] {

  return AccountsListSchema.parse(response);
}


/**
 * Builds the axios request to load all accounts of a Hebrew account type.
 * @param accountHebType AccountHebType - Account type label (e.g. income, bank).
 * @returns object - URL, method, and query params for GET accounts.
 */
function getAccountsByTypeQuery(accountHebType: AccountHebType) {

  return {
    url: "accounts",
    method: "GET" as const,
    params: {
      "filter[account_type]": accountHebType,
      include: "customData,entries.document.documentSettings",
      sort: "id",
      use_cache: 1,
    },
  };
}


/**
 * Resolves RTK Query cache tags for accounts loaded by type.
 * @param _res Account[] | undefined - Successful query result (unused).
 * @param _err unknown - Query error (unused).
 * @param accountHebType AccountHebType - Account type from query args.
 * @returns Array - Cache tag keyed by account type.
 */
function getAccountsByTypeProvidesTags(_res: Account[] | undefined, _err: unknown, accountHebType: AccountHebType) {

  return [{ type: "accounts" as const, id: accountHebType }];
}


/**
 * Builds a stable cache key for accounts browse queries with object args.
 * @param endpointName string - RTK Query endpoint name.
 * @param queryArgs SearchAccountsBrowseArgs - Browse filters, includes, sort, and pagination.
 * @returns string - Serialized cache key for the query args.
 */
function searchAccountsBrowseSerializeQueryArgs({ endpointName, queryArgs }: { endpointName: string; queryArgs: SearchAccountsBrowseArgs }): string {
  const { useCache: _omit, ...cacheKeyArgs } = queryArgs;

  return `${endpointName}:${stableSerialize(cacheKeyArgs)}`;
}


/**
 * Builds the axios request for accounts browse search.
 * @param arg SearchAccountsBrowseArgs - Account type, filters, includes, and pagination.
 * @returns object - URL, method, and POST body for accounts/search.
 */
function searchAccountsBrowseQuery({ accountType, filter, withRelations, include, per_page, useCache, ...rest }: SearchAccountsBrowseArgs) {

  return {
    url: "accounts/search",
    method: "POST" as const,
    // Apply browse defaults and normalize filter for Spatie search API
    data: getAccountSearchBody({
      filter: { account_type: accountType, ...filter },
      include: include ?? (withRelations ? ["customData"] : undefined),
      per_page: per_page ?? ACCOUNT_BROWSE_PER_PAGE,
      useCache,
      ...rest,
    }),
  };
}


/**
 * Validates accounts browse response; uses relations schema when requested.
 * @param response unknown - Raw response body from axiosBaseQuery.
 * @param _meta unknown - RTK Query meta (unused).
 * @param arg SearchAccountsBrowseArgs - Original query args (checks withRelations).
 * @returns SearchAccountsBrowseResponse - Parsed paginated accounts response with rows and cursor meta.
 */
function searchAccountsBrowseTransformResponse(response: unknown, _meta: unknown, arg: SearchAccountsBrowseArgs): SearchAccountsBrowseResponse {

  if (arg.withRelations) {
    const parsed = AccountsPaginatedWithRelationsSchema.parse(response);

    return {
      ...parsed,
      data: parsed.data as Account[],
    };
  }

  return AccountsPaginatedSchema.parse(response);
}


/**
 * Resolves RTK Query cache tags for accounts browse by account type.
 * @param _res SearchAccountsBrowseResponse | undefined - Successful query result (unused).
 * @param _err unknown - Query error (unused).
 * @param arg SearchAccountsBrowseArgs - Browse args containing accountType.
 * @returns Array - Cache tag keyed by browse account type.
 */
function searchAccountsBrowseProvidesTags(_res: SearchAccountsBrowseResponse | undefined, _err: unknown, arg: SearchAccountsBrowseArgs) {

  return [{ type: "accountsBrowse" as const, id: arg.accountType }];
}


/**
 * Builds the axios request to fetch a single account by id.
 * @param accountId string | number - Account primary key.
 * @returns object - URL and method for GET accounts/:id.
 */
function getAccountByIdQuery(accountId: string | number) {

  return {
    url: `accounts/${accountId}`,
    method: "GET" as const,
    params: {
      include: "customData",
    },
  };
}


/**
 * Resolves RTK Query cache tags for a single account detail query.
 * @param _res Account | undefined - Successful query result (unused).
 * @param _err unknown - Query error (unused).
 * @param accountId string | number - Account primary key from query args.
 * @returns Array - Cache tag for the account detail entity.
 */
function getAccountByIdProvidesTags(_res: Account | undefined, _err: unknown, accountId: string | number) {

  return [{ type: "account" as const, id: Number(accountId) }];
}


/**
 * Builds the axios request to create an account.
 * @param accountToAdd AccountAdd - Payload for the new account.
 * @returns object - URL, method, and POST body for accounts.
 */
function addAccountQuery(accountToAdd: AccountAdd) {

  return {
    url: "accounts",
    method: "POST" as const,
    data: accountToAdd,
  };
}


/**
 * Resolves RTK Query cache tags to invalidate after creating an account.
 * @param _res Account | undefined - Successful mutation result (unused).
 * @param _err unknown - Mutation error (unused).
 * @param accountToAdd AccountAdd - Created account payload (uses account_type).
 * @returns Array - Cache tags for typed lists and browse lists.
 */
function addAccountInvalidatesTags(_res: Account | undefined, _err: unknown, accountToAdd: AccountAdd) {

  return accountMutationTags({
    accountType: accountToAdd.account_type,
  });
}


/**
 * Builds the axios request to update an account.
 * @param arg object - Account id and PATCH payload.
 * @returns object - URL, method, and PATCH body for accounts/:id.
 */
function saveAccountQuery({ accountId, accountToSave }: { accountId: number; accountToSave: AccountEdit; accountType?: AccountBrowseType }) {

  return {
    url: `accounts/${accountId}`,
    method: "PATCH" as const,
    data: accountToSave,
  };
}


/**
 * Resolves RTK Query cache tags to invalidate after updating an account.
 * @param _res Account | undefined - Successful mutation result (unused).
 * @param _err unknown - Mutation error (unused).
 * @param arg object - Mutation arg with accountId and optional accountType.
 * @returns Array - Cache tags for lists and the updated account detail.
 */
function saveAccountInvalidatesTags(_res: Account | undefined, _err: unknown, { accountId, accountType }: { accountId: number; accountToSave: AccountEdit; accountType?: AccountBrowseType }) {

  return accountMutationTags({
    accountType,
    accountId,
  });
}


/**
 * Builds the axios request to delete an account.
 * @param arg object - Account id (and optional type for cache invalidation).
 * @returns object - URL and method for DELETE accounts/:id.
 */
function deleteAccountQuery({ accountId }: { accountId: number; accountType?: AccountBrowseType }) {

  return {
    url: `accounts/${accountId}`,
    method: "DELETE" as const,
  };
}


/**
 * Resolves RTK Query cache tags to invalidate after deleting an account.
 * @param _res void | undefined - Successful mutation result (unused).
 * @param _err unknown - Mutation error (unused).
 * @param arg object - Mutation arg with accountId and optional accountType.
 * @returns Array - Cache tags for lists and the deleted account detail.
 */
function deleteAccountInvalidatesTags(_res: void | undefined, _err: unknown, { accountId, accountType }: { accountId: number; accountType?: AccountBrowseType }) {

  return accountMutationTags({
    accountType,
    accountId,
  });
}


/**
 * Builds the axios request for the next account number for a type.
 * @param accountType AccountBrowseType - Hebrew account type label.
 * @returns object - URL, method, and POST body for accounts/next-number.
 */
function getNextAccountNumberQuery(accountType: AccountBrowseType) {

  return {
    url: "accounts/next-number",
    method: "POST" as const,
    data: { account_type: accountType },
  };
}


/**
 * Maps the next-number API payload to a numeric account index.
 * @param response object - Raw response with next_account_index.
 * @returns number - Next account number from the server.
 */
function getNextAccountNumberTransformResponse(response: { next_account_index: number }): number {

  return response.next_account_index;
}


/**
 * Builds the axios request for a recommended account number by sort code.
 * @param sortCode number - Sort code used to suggest the next account number.
 * @returns object - URL, method, and POST body for accounts/recommendation.
 */
function accountRecommendationQuery(sortCode: number) {

  return {
    url: "accounts/recommendation",
    method: "POST" as const,
    data: { sort_code: sortCode },
  };
}


/**
 * Maps the recommendation API payload to a numeric account number.
 * @param response object - Raw response with recommended_account_number.
 * @returns number - Recommended account number from the server.
 */
function accountRecommendationTransformResponse(response: { recommended_account_number: number }): number {

  return response.recommended_account_number;
}
